// React Query hooks for transactions. Thin wrappers over lib/api/transactions.ts.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createTransaction,
  getTransaction,
  listTransactionsByEvent,
  listRecentTransactions,
  listTransactionsByCustomer,
  updateFulfillment,
  updateTransaction,
  voidTransaction,
  previewNextInvoiceNumber,
  type CreateTransactionInput,
  type UpdateFulfillmentInput,
  type UpdateTransactionInput,
  type VoidTransactionInput,
} from '@/lib/api/transactions';

const KEYS = {
  all: ['transactions'] as const,
  listByEvent: (eventId: string) => [...KEYS.all, 'list', eventId] as const,
  recent: (limit: number) => [...KEYS.all, 'recent', limit] as const,
  byCustomer: (customerId: string, limit: number) =>
    [...KEYS.all, 'by-customer', customerId, limit] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  previewInvoice: (eventId: string) =>
    [...KEYS.all, 'preview-invoice', eventId] as const,
};

export const transactionKeys = KEYS;

// ==== Queries ====

export const useTransaction = (id: string | null) =>
  useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => (id ? getTransaction(id) : Promise.resolve(null)),
    enabled: !!id,
  });

export const useTransactionsByEvent = (eventId: string | null | undefined) =>
  useQuery({
    queryKey: KEYS.listByEvent(eventId ?? ''),
    queryFn: () =>
      eventId ? listTransactionsByEvent(eventId) : Promise.resolve([]),
    enabled: !!eventId,
  });

/**
 * Recent transactions across all events. Used by the /transactions page
 * when no active event is selected.
 */
export const useRecentTransactions = (limit = 50) =>
  useQuery({
    queryKey: KEYS.recent(limit),
    queryFn: () => listRecentTransactions(limit),
  });

/**
 * All transactions for a specific customer, newest first.
 * Powers the /customers/:id detail page.
 */
export const useTransactionsByCustomer = (
  customerId: string | null | undefined,
  limit = 200,
) =>
  useQuery({
    queryKey: KEYS.byCustomer(customerId ?? '', limit),
    queryFn: () =>
      customerId
        ? listTransactionsByCustomer(customerId, limit)
        : Promise.resolve([]),
    enabled: !!customerId,
  });

/** Preview-only next invoice number for the given event. May drift before creation. */
export const useNextInvoiceNumber = (eventId: string | null | undefined) =>
  useQuery({
    queryKey: KEYS.previewInvoice(eventId ?? ''),
    queryFn: () =>
      eventId ? previewNextInvoiceNumber(eventId) : Promise.resolve(''),
    enabled: !!eventId,
    staleTime: 5 * 1000,
  });

// ==== Mutations ====

export const useCreateTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTransactionInput) => createTransaction(input),
    onSuccess: (result, vars) => {
      // Refresh everything that might have changed.
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({
        queryKey: ['products', 'list', { isActive: true }],
      });
      qc.invalidateQueries({ queryKey: ['products', 'list', {}] });
      // Product stock changed → stock movements per product also stale.
      for (const item of vars.items) {
        qc.invalidateQueries({
          queryKey: ['products', 'movements', item.productId],
        });
      }
      // The Firestore version pre-populated the detail cache from the input
      // so the post-POS navigation felt instant. That is no longer safe: the
      // client does not compute prices, discount, tax or grand total any more
      // — Postgres does, from the event's allocation. Reconstructing them here
      // would show the customer numbers the invoice does not actually carry.
      // Invalidate and let the real row load instead.
      qc.invalidateQueries({ queryKey: KEYS.detail(result.id) });
    },
  });
};

export const useUpdateFulfillment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; input?: UpdateFulfillmentInput }) =>
      updateFulfillment(id),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
};

/**
 * General-purpose transaction edit (notes, payment method, payment status).
 * Use this for manual corrections by owner. Invalidates all transaction
 * queries so list + detail views both refresh.
 */
export const useUpdateTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTransactionInput }) =>
      updateTransaction(id, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
};

/**
 * Void a sale. Stock went back to its lots and the event's totals moved, so
 * everything that reads either is stale — not just the transaction itself.
 */
export const useVoidTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VoidTransactionInput) => voidTransaction(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event-allocations'] });
      qc.invalidateQueries({ queryKey: ['eventAllocations'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['receivables'] });
    },
  });
};
